import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ClientError } from '~/errors';
import { sanitizeTagValue } from '~/utils/instrumentation';
import { createLogger } from '~/utils/logging';
import { useHttpClient } from '~/hooks/useHttpClient';
import { useLocale } from '~/hooks/useLocale';
import {
  REVIEW_ACTION_ITEMS_QUERY_KEY,
  REVIEW_CLASS_SUMMARY_QUERY_KEY,
  REVIEW_DATA_LOG_QUERY_KEY,
  REVIEW_SCORECARDS_QUERY_KEY,
} from '~/services/review';
import { formatDateToYearMonthDayString } from '~/utils/date';

const log = createLogger('useReviewDeleteMutation');

export type DeleteReviewRequest = {
  periodId: string;
  date: Date;
};

export type DeleteReviewResponse = {
  deletedSessions: {
    id: string;
  };
};

export function useReviewDeleteMutation() {
  const httpClient = useHttpClient();
  const queryClient = useQueryClient();
  const locale = useLocale();

  const { data, error, mutate, reset, status } = useMutation({
    mutationFn: async (request: DeleteReviewRequest) => {
      log.debug('Deleting review', { request });
      const { date, periodId } = request;

      const requestBody = {
        date: formatDateToYearMonthDayString(date),
        periodId,
        tz: locale?.timeZone ?? '',
      };

      const { deletedSessions } = await httpClient.post<DeleteReviewResponse>('/review/delete', requestBody);
      if (deletedSessions) {
        log.info('Deleted review successfully', {
          sessionId: deletedSessions.id,
        });
      }
      return { deletedSessions };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REVIEW_ACTION_ITEMS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [REVIEW_CLASS_SUMMARY_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [REVIEW_DATA_LOG_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [REVIEW_SCORECARDS_QUERY_KEY] });
    },
    onError: (error: Error) => {
      if (error instanceof ClientError) {
        // Ignore client errors.
      } else {
        log.error('Failed to delete review', {
          errorMessage: sanitizeTagValue(error.message),
        });
      }
    },
  });

  return {
    data,
    error,
    mutate,
    reset,
    status,
  };
}
